import { supabase } from './supabase'
import {
  matchesDivisionWithFallback,
  type DivisionSelection,
} from './divisionFiltering'

const QC_EMBED_SELECT = `id, scheduled_date, inspection_date, status, priority, overall_rating, findings, requires_followup, followup_date, notes,
    contract_id, job_id, inspector_id,
    contract:contracts(contract_number, title, operational_division_id, service_type),
    job:jobs(title, job_number, operational_division_id, service_type),
    inspector:employees(first_name, last_name)`

const QC_BARE_SELECT = `id, scheduled_date, inspection_date, status, priority, overall_rating, findings, requires_followup, followup_date, notes,
    contract_id, job_id, inspector_id`

function isMissingRelationshipError(message?: string | null): boolean {
  return /relationship|schema cache/i.test(message || '')
}

function normalizeEmbeds(row: any) {
  return {
    ...row,
    contract: Array.isArray(row.contract) ? row.contract[0] || null : row.contract ?? null,
    job: Array.isArray(row.job) ? row.job[0] || null : row.job ?? null,
    inspector: Array.isArray(row.inspector) ? row.inspector[0] || null : row.inspector ?? null,
  }
}

/** Hydrate contract/job/inspector when PostgREST embeds are unavailable. */
async function hydrateQcRelations(rows: any[]): Promise<any[]> {
  if (!rows.length) return []

  const contractIds = Array.from(new Set(rows.map((r) => r.contract_id).filter(Boolean)))
  const jobIds = Array.from(new Set(rows.map((r) => r.job_id).filter(Boolean)))
  const inspectorIds = Array.from(new Set(rows.map((r) => r.inspector_id).filter(Boolean)))

  const [contractsRes, jobsRes, inspectorsRes] = await Promise.all([
    contractIds.length
      ? supabase.from('contracts').select('id, contract_number, title, operational_division_id, service_type').in('id', contractIds)
      : Promise.resolve({ data: [] as any[] }),
    jobIds.length
      ? supabase.from('jobs').select('id, title, job_number, operational_division_id, service_type').in('id', jobIds)
      : Promise.resolve({ data: [] as any[] }),
    inspectorIds.length
      ? supabase.from('employees').select('id, first_name, last_name').in('id', inspectorIds)
      : Promise.resolve({ data: [] as any[] }),
  ])

  const contracts = Object.fromEntries((contractsRes.data || []).map((c: any) => [c.id, c]))
  const jobs = Object.fromEntries((jobsRes.data || []).map((j: any) => [j.id, j]))
  const inspectors = Object.fromEntries((inspectorsRes.data || []).map((e: any) => [e.id, e]))

  return rows.map((row) => ({
    ...row,
    contract: row.contract_id ? contracts[row.contract_id] || null : null,
    job: row.job_id ? jobs[row.job_id] || null : null,
    inspector: row.inspector_id ? inspectors[row.inspector_id] || null : null,
  }))
}

type QcListQuery = {
  statusEq?: string
  statusIn?: string[]
  orderBy: string
  ascending?: boolean
  limit?: number
  division?: DivisionSelection | null
}

function filterAndLimit(rows: any[], query: QcListQuery): any[] {
  const filtered = rows.filter((row) =>
    matchesDivisionWithFallback(row.contract, query.division, row.job)
  )
  return query.limit == null ? filtered : filtered.slice(0, query.limit)
}

/**
 * Load QC inspections with contract/job/inspector embeds.
 * Falls back to bare select + manual join when FK relationship is missing from schema cache.
 */
export async function fetchQcInspectionsList(query: QcListQuery): Promise<{ data: any[]; error: any }> {
  const applyFilters = (builder: any) => {
    let q = builder.select(QC_EMBED_SELECT)
    if (query.statusEq) q = q.eq('status', query.statusEq)
    if (query.statusIn) q = q.in('status', query.statusIn)
    q = q.order(query.orderBy, { ascending: query.ascending ?? true })
    if (query.limit != null && !query.division) q = q.limit(query.limit)
    return q
  }

  const applyBareFilters = (builder: any) => {
    let q = builder.select(QC_BARE_SELECT)
    if (query.statusEq) q = q.eq('status', query.statusEq)
    if (query.statusIn) q = q.in('status', query.statusIn)
    q = q.order(query.orderBy, { ascending: query.ascending ?? true })
    if (query.limit != null && !query.division) q = q.limit(query.limit)
    return q
  }

  let { data, error } = await applyFilters(supabase.from('qc_inspections'))

  if (error && isMissingRelationshipError(error.message)) {
    ;({ data, error } = await applyBareFilters(supabase.from('qc_inspections')))
    if (error) return { data: [], error }
    const hydrated = await hydrateQcRelations(data || [])
    return { data: filterAndLimit(hydrated, query), error: null }
  }

  if (error) return { data: [], error }
  return { data: filterAndLimit((data || []).map(normalizeEmbeds), query), error: null }
}
